<template>
  <LayoutLogin>
    <template v-slot:content>
      <div v-if="progressBarIsShowing">
        <v-progress-linear
          :active="true"
          :background-opacity="0.3"
          :bottom="false"
          :buffer-value="100"
          :height="8"
          :indeterminate="true"
          :query="false"
          :rounded="true"
          :stream="false"
          :striped="false"
          :top="false"
          :value="0"
          color="light-blue"
        />
        <p class="h6 text-center white--text">{{ $t(waitingMessage) }}</p>
      </div>
      <LayoutLoginCard v-else>
        <Logo class="mb-6 mx-auto text-center" type="big" />
        <GoTo
          v-if="currentStep === 2"
          class="my-4"
          testId="signUp-button-backLogin"
          :linkMessage="$t('common.button.back')"
          @onClickGoTo="currentStep = 1"
        />
        <p class="body1 text-center mb-6" data-private>
          {{ $t('signup.create_your_account') }}
        </p>
        <StepMarker
          class="mb-6"
          :label="$t('common.step', { step: currentStep })"
          :step="currentStep"
        />
        <v-form v-model="isFormValid" ref="signUpForm">
          <SignUpRegisterForm
            v-if="currentStep === 1"
            showGDPRCheckbox
            showTermsOfService
            :isFormValid="isFormValid"
            :errors="errors"
            :account="account"
            :buttonText="$t('common.button.next')"
            :organization="organization"
            @onSubmit="onSubmitFirstStep"
            @onChange="handleChange"
            @onChangePassword="account.password = $event"
          />
          <CreateOrganizationStep
            v-if="currentStep === 2"
            :showErrorAlert="showErrorAlert"
            :account="account"
            :organization="organization"
            @createOrganizationSubmit="submitForm"
          />
        </v-form>
      </LayoutLoginCard>
    </template>
  </LayoutLogin>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import config from '@/config.js'
import cookie from 'cookie'
import mixin from '@/mixins/mixin.vue'
import authMixin from '@/mixins/authMixin.vue'
import { load } from 'recaptcha-v3'

import LayoutLogin from '@/components/Core/Layout/LayoutLogin'
import Logo from '@/components/Core/Logo/Logo.vue'
import SignUpRegisterForm from './components/SignUpRegisterForm.vue'
import CreateOrganizationStep from './components/CreateOrganizationStep.vue'
import StepMarker from './components/StepMarker.vue'
import LayoutLoginCard from './components/LayoutLoginCard.vue'
import GoTo from './components/GoTo.vue'

const FIELDS = {
  email: 'email',
  organizationName: 'organizationName',
}

export default {
  props: [],

  components: {
    LayoutLogin,
    Logo,
    StepMarker,
    SignUpRegisterForm,
    CreateOrganizationStep,
    LayoutLoginCard,
    GoTo,
  },

  mixins: [mixin, authMixin],

  data() {
    return {
      isFormValid: false,
      currentStep: 1,
      account: {
        _id: null,
        email: null,
        displayname: null,
        familyname: null,
        password: null,
        receiveEmailReports: false,
        receiveEmailNotifications: false,
        gdprApproval: false,
        status: null,
      },
      organization: {
        name: '',
        site: {
          countryCode: '',
        },
        industry: null,
      },
      errors: {
        email: null,
        organizationName: null,
      },
      progressBarIsShowing: false,
      waitingMessage: null,
      showErrorAlert: false,
    }
  },

  created() {
    this.$enableAnonymousTracking(this.$store)

    this.$nextTick(() => {
      this.$track('signup.Start', this.$store, {
        process: 'started',
        status: 'success',
      })
    })

    this.populateFieldsFromURL()
  },

  computed: {
    ...mapState({
      access: state => state.access,
    }),

    defaultLanguage() {
      const defaultLanguage = navigator.language || navigator.userLanguage
      return defaultLanguage.substring(0, 2)
    },
  },

  methods: {
    ...mapActions({
      signup: 'signup',
      verifyOrganization: 'verifyOrganization',
    }),

    populateFieldsFromURL() {
      const { query } = this.$route

      this.organization.name = query.organization_name || null
      this.account.email = query.account_email || null
      this.account.displayname = query.account_displayname || null
      this.account.familyname = query.account_familyname || null
    },

    async onSubmitFirstStep() {
      try {
        this.$refs.signUpForm.validate()
        await this.$nextTick()

        if (!this.isFormValid) return

        this.progressBarIsShowing = true
        this.waitingMessage = this.$t('signup.validating_your_data')

        await this.validateOrganization()
        this.currentStep = 2

        this.$track('signup.Create', this.$store, {
          process: 'submitFirstStep',
          status: 'success',
          organizationName: this.organization.name,
          accountEmail: this.account.email,
        })

        this.progressBarIsShowing = false
        this.waitingMessage = null
      } catch (err) {
        this.showErrorAlert = true

        this.$track('signup.Create', this.$store, {
          process: 'submitFirstStep',
          status: 'fail',
          error: err.message,
        })

        this.progressBarIsShowing = false
        this.waitingMessage = null
        this.account.password = null
      }
    },

    async validateOrganization() {
      const RECAPTCHA_SITE_KEY = config.recaptchaToken
      const recaptcha = await load(RECAPTCHA_SITE_KEY)
      const token = await recaptcha.execute('submit')

      const validation = await this.verifyOrganization({
        name: this.organization.name,
        domain: this.getDomainOfEmail(this.account.email),
        recaptcha: token,
      })

      if (validation.data.result === 'error') {
        const { message } = validation.data

        if (message === 'domain_exists') {
          this.errors.email = this.$t('signup.domain_exists')
        }

        if (message === 'name_exists') {
          this.errors.organizationName = this.$t('signup.name_exists')
        }

        if (message === 'domain_is_not_business_email') {
          this.errors.email = this.$t('signup.domain_is_not_business_email')
        }

        if (message === 'failed_captcha') {
          this.errors.email = this.$t('signup.failed_captcha')
        }

        const unknownError = `signup.${validation.data.message}`
        throw new Error(unknownError)
      }

      return true
    },

    getDomainOfEmail(email) {
      const domain = email.replace(/.*@/, '')
      return domain
    },

    async submitForm() {
      const TWO_SECONDS = 2000
      const FIFTEEN_SECONDS = 15000

      this.waitingMessage = 'signup.creating_your_account'
      this.progressBarIsShowing = true
      let response

      setTimeout(async () => {
        this.waitingMessage = 'signup.a_few_more_seconds'
        response = await this.doSignup()
      }, TWO_SECONDS)

      setTimeout(async () => {
        this.progressBarIsShowing = false
        if (response.data) {
          this.$track('signup.Create', this.$store, {
            process: 'submitSecondStep',
            status: 'success',
            organizationName: this.organization.name,
            accountEmail: this.account.email,
            industry: this.organization?.industry,
          })

          return this.login(response.data)
        }
      }, FIFTEEN_SECONDS)
    },

    async doSignup() {
      const { tz } = await import('moment-timezone')

      try {
        const payload = {
          organization: {
            name: this.organization.name,
            industry: this.organization.industry,
            language: this.defaultLanguage,
            domains: [this.getDomainOfEmail(this.account.email)],
          },
          site: {
            name: this.organization.name,
            countryCode: this.organization.site.countryCode.code,
            timezone: tz.guess(),
          },
          account: this.account,
        }

        payload.account.cookie = this.getHubspotCookie()

        const response = await this.signup(payload)
        return response
      } catch (error) {
        this.showErrorAlert = true
        this.$track('signup.Create', this.$store, {
          process: 'submitSecondStep',
          status: 'fail',
          organizationName: this.organization.name,
          accountEmail: this.account.email,
        })

        throw error
      }
    },

    getHubspotCookie() {
      const cookies = cookie.parse(document.cookie)
      const COOKIE_NAME = 'hubspotutk'
      return cookies[COOKIE_NAME] || null
    },

    login(payload) {
      this.setLoginData(payload)
      return this.navigateTo('/home')
    },

    handleChange(data, field) {
      if (field === FIELDS.email) {
        this.account.email = data
        this.errors.email = ''
      }

      if (field === FIELDS.organizationName) {
        this.organization.name = data
        this.errors.organizationName = ''
      }
    },
  },
}
</script>
